import { useState, useEffect } from 'react';
import { permissionsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Request,
  RequestType,
  RequestStatus,
  Role,
  getRequestTypeName,
} from '../types/models';
import { format } from 'date-fns';
import {
  FileText,
  Plus,
  Check,
  X,
  Clock,
  Calendar,
} from 'lucide-react';

export default function PermissionsPage() {
  const { hasRole } = useAuth();
  const [myRequests, setMyRequests] = useState<Request[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Request[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type_id: RequestType.VACATION,
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const myReqs = await permissionsApi.getMyRequests() as Request[];
      setMyRequests(myReqs || []);

      if (hasRole([Role.ADMIN, Role.JEFE_TALLER])) {
        const pending = await permissionsApi.getPendingRequests() as Request[];
        setPendingRequests(pending || []);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await permissionsApi.createRequest(formData);
      setShowForm(false);
      setFormData({
        type_id: RequestType.VACATION,
        start_date: '',
        end_date: '',
        reason: '',
      });
      await loadData();
    } catch (error) {
      console.error('Error creating request:', error);
      alert(error instanceof Error ? error.message : 'Error al crear solicitud');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      await permissionsApi.approveRequest(requestId);
      await loadData();
    } catch (error) {
      console.error('Error approving:', error);
    }
  };

  const handleDeny = async (requestId: string) => {
    const reason = prompt('Motivo del rechazo:');
    if (reason === null) return;
    try {
      await permissionsApi.denyRequest(requestId, reason);
      await loadData();
    } catch (error) {
      console.error('Error denying:', error);
    }
  };

  const handleCancel = async (requestId: string) => {
    if (!confirm('¿Cancelar esta solicitud?')) return;
    try {
      await permissionsApi.cancelRequest(requestId);
      await loadData();
    } catch (error) {
      console.error('Error canceling:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Permisos y Vacaciones
          </h1>
          <p className="text-gray-500">Gestiona tus solicitudes de tiempo libre</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Nueva Solicitud
        </button>
      </div>

      {/* New Request Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Nueva Solicitud</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Permiso
                </label>
                <select
                  value={formData.type_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, type_id: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value={RequestType.VACATION}>Vacaciones</option>
                  <option value={RequestType.SICK}>Enfermedad</option>
                  <option value={RequestType.PERSONAL}>Personal</option>
                  <option value={RequestType.BEREAVEMENT}>Duelo</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe el motivo de tu solicitud..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Queue (for Admin/Jefe) */}
      {hasRole([Role.ADMIN, Role.JEFE_TALLER]) && pendingRequests.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Pendientes de Aprobación ({pendingRequests.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingRequests.map((request) => (
              <div key={request.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-800">
                      {getRequestTypeName(request.type_id as RequestType)}
                    </span>
                    <span className="text-sm text-gray-500 ml-2">
                      {request.days_count} día(s)
                    </span>
                  </div>
                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                    Pendiente
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(request.start_date), 'dd MMM')} - {format(new Date(request.end_date), 'dd MMM yyyy')}
                  </span>
                </div>
                {request.reason && (
                  <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-2 rounded">
                    {request.reason}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(request.id)}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                  >
                    <Check className="h-4 w-4" />
                    Aprobar
                  </button>
                  <button
                    onClick={() => handleDeny(request.id)}
                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1"
                  >
                    <X className="h-4 w-4" />
                    Rechazar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Requests */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Mis Solicitudes</h2>
        </div>

        {myRequests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No tienes solicitudes registradas
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {myRequests.map((request) => (
              <div key={request.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-800">
                      {getRequestTypeName(request.type_id as RequestType)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      request.status_id === RequestStatus.APPROVED
                        ? 'bg-green-100 text-green-700'
                        : request.status_id === RequestStatus.DENIED
                        ? 'bg-red-100 text-red-700'
                        : request.status_id === RequestStatus.CANCELLED
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {request.status_id === RequestStatus.APPROVED
                        ? 'Aprobado'
                        : request.status_id === RequestStatus.DENIED
                        ? 'Rechazado'
                        : request.status_id === RequestStatus.CANCELLED
                        ? 'Cancelado'
                        : 'Pendiente'}
                    </span>
                  </div>
                  {request.status_id === RequestStatus.PENDING && (
                    <button
                      onClick={() => handleCancel(request.id)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Cancelar
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(request.start_date), 'dd MMM')} - {format(new Date(request.end_date), 'dd MMM yyyy')}
                  </span>
                  <span>{request.days_count} día(s)</span>
                </div>

                {request.reason && (
                  <p className="text-sm text-gray-600 mt-2">{request.reason}</p>
                )}

                {request.approver_note && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <strong>Respuesta:</strong> {request.approver_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
